import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { products, Product } from '@/mocks/products';
import ProductQuickView from '@/components/feature/ProductQuickView';

const brands = [
  {
    name: 'Faasley – Break Time',
    tagline: 'Har Break ka Healthy Partner',
    desc: 'Premium healthy snacking — elegant, modern, travel-friendly packaging.',
    badge: 'Featured Brand',
    image: 'https://static.readdy.ai/image/3301efb4abc894ce1097fc48d2d46708/6cabdef9482c1e4e3a79baae13ecbe41.png',
    category: 'Makhana',
  },
  {
    name: 'Sachar – Mera Achar',
    tagline: 'Traditional Homemade Pickles',
    desc: 'Authentic Indian taste with premium gifting potential.',
    badge: 'Bestseller',
    image: 'https://storage.readdy-site.link/project_files/c44a33b9-73a7-4f29-8c86-80a04dae53c1/44a51372-8cc7-4c48-a303-2b649bb3609a_WhatsApp-Image-2026-04-02-at-11.18.47-PM.jpeg?v=6a7bc716aaf61222a1bd0dd083975c40',
    category: 'Pickles',
  },
  {
    name: 'Watler',
    tagline: 'Pure Refreshing Water',
    desc: 'Clean, refreshing packaged water with a strong modern identity.',
    badge: 'New',
    image: 'https://storage.readdy-site.link/project_files/c44a33b9-73a7-4f29-8c86-80a04dae53c1/96640edd-8e88-4367-8213-f4dd46d2a40d_Walter.jpg?v=455f1e923d8920a21fb1dcd822d91d47',
    category: 'Water',
  },
  {
    name: 'Faasnut',
    tagline: 'Healthy Makhana Snacks',
    desc: 'Light, premium, wellness-focused makhana range.',
    badge: 'Popular',
    image: 'https://static.readdy.ai/image/3301efb4abc894ce1097fc48d2d46708/6cabdef9482c1e4e3a79baae13ecbe41.png',
    category: 'Makhana',
  },
  {
    name: 'Booston Tea',
    tagline: 'Bold Flavour, Every Cup',
    desc: 'Bold tea identity built for warmth and premium daily experience.',
    badge: 'Trending',
    image: 'https://static.readdy.ai/image/3301efb4abc894ce1097fc48d2d46708/1c85c6075a2c1d4ce0f69fc4c8988ca2.png',
    category: 'Tea',
  },
];

const categories = ['all', 'Makhana', 'Snacks', 'Spices', 'Tea', 'Pickles', 'Water'];

export default function ProductsSection() {
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const navigate = useNavigate();

  const filteredProducts = activeCategory === 'all'
    ? products
    : products.filter((p) => p.category === activeCategory);

  const openQuickView = (category: string) => {
    const p = products.find((pr) => pr.category === category);
    if (p) setQuickViewProduct(p);
  };

  return (
    <>
      {/* ── BRAND SPOTLIGHT — warm cream ── */}
      <section style={{ background: 'linear-gradient(to bottom, #faf7f2, #f5ede0)' }} className="py-16 md:py-20">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20 max-w-6xl mx-auto">

          {/* ── Section Header ── */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px flex-1 max-w-[80px]" style={{ background: '#c9a96e' }}></div>
            <i className="ri-flower-line text-base" style={{ color: '#c9a96e' }}></i>
            <h2
              className="font-bold text-center px-2"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.6rem', color: '#2c1a0e' }}
            >
              Our Brands
            </h2>
            <i className="ri-flower-line text-base" style={{ color: '#c9a96e' }}></i>
            <div className="h-px flex-1 max-w-[80px]" style={{ background: '#c9a96e' }}></div>
          </div>

          <p className="text-center text-sm mb-3" style={{ color: '#7a4f2a' }}>
            9+ premium products across 5 categories — authentic taste, hygienic packaging, integrity in every bite.
          </p>

          {/* Decorative divider */}
          <div className="flex items-center justify-center gap-2 mb-12">
            <div className="h-px w-24" style={{ background: '#e8d5b7' }}></div>
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#c9a96e' }}></div>
            <div className="h-px w-24" style={{ background: '#e8d5b7' }}></div>
          </div>

          {/* ── MAIN GRID ── */}
          <div className="grid lg:grid-cols-5 gap-5 mb-5">

            {/* Big featured card */}
            <div
              className="lg:col-span-3 relative rounded-2xl overflow-hidden cursor-pointer group"
              style={{ minHeight: '480px', border: '1px solid #e8d5b7' }}
              onClick={() => openQuickView('Makhana')}
            >
              <img
                src="https://static.readdy.ai/image/3301efb4abc894ce1097fc48d2d46708/6cabdef9482c1e4e3a79baae13ecbe41.png"
                alt="Faasley Break Time"
                className="w-full h-full object-cover absolute inset-0 transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(44,26,14,0.82) 0%, rgba(44,26,14,0.15) 55%, transparent 100%)' }}></div>

              {/* Badge */}
              <div className="absolute top-5 left-5">
                <span
                  className="text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full"
                  style={{ background: '#faf7f2', color: '#3b2010' }}
                >
                  Featured Brand
                </span>
              </div>

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-7">
                <div className="rounded-xl p-5" style={{ background: 'rgba(250,247,242,0.95)', border: '1px solid #e8d5b7' }}>
                  <p className="text-xs font-bold uppercase tracking-widest mb-1.5" style={{ color: '#c9a96e' }}>Spotlight</p>
                  <h3
                    className="font-bold mb-1"
                    style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.3rem', color: '#2c1a0e' }}
                  >
                    Faasley – Break Time
                  </h3>
                  <p className="text-xs italic mb-4" style={{ color: '#7a4f2a' }}>"Har Break ka Healthy Partner"</p>
                  <div className="flex items-center gap-3">
                    <Link
                      to="/products"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-2 font-semibold px-6 py-2.5 rounded-md text-sm transition-all whitespace-nowrap"
                      style={{ background: '#3b2010', color: '#faf7f2' }}
                    >
                      Shop Now <i className="ri-arrow-right-line"></i>
                    </Link>
                    <Link
                      to="/about"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-2 font-medium px-6 py-2.5 rounded-md text-sm transition-all whitespace-nowrap"
                      style={{ border: '1px solid #c9a96e', color: '#3b2010', background: 'transparent' }}
                    >
                      Our Story
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Right stack — 2 cards */}
            <div className="lg:col-span-2 flex flex-col gap-5">
              {brands.slice(1, 3).map((brand) => (
                <div
                  key={brand.name}
                  className="relative rounded-2xl overflow-hidden cursor-pointer group flex-1"
                  style={{ minHeight: '220px', border: '1px solid #e8d5b7' }}
                  onClick={() => openQuickView(brand.category)}
                >
                  <img
                    src={brand.image}
                    alt={brand.name}
                    className="w-full h-full object-cover absolute inset-0 transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(44,26,14,0.85) 0%, rgba(44,26,14,0.1) 60%, transparent 100%)' }}></div>

                  <div className="absolute top-4 left-4">
                    <span
                      className="text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full"
                      style={{ background: 'rgba(250,247,242,0.92)', color: '#3b2010' }}
                    >
                      {brand.badge}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3
                      className="font-bold mb-0.5"
                      style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.1rem', color: '#faf7f2' }}
                    >
                      {brand.name}
                    </h3>
                    <p className="text-xs italic" style={{ color: '#e8d5b7' }}>{brand.tagline}</p>
                    <div
                      className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold group-hover:gap-3 transition-all"
                      style={{ color: '#c9a96e' }}
                    >
                      Explore <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom row — 3 equal cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-product-shop>
            {brands.slice(2, 5).map((brand) => (
              <div
                key={brand.name}
                className="relative rounded-2xl overflow-hidden cursor-pointer group"
                style={{ minHeight: '250px', border: '1px solid #e8d5b7' }}
                onClick={() => openQuickView(brand.category)}
              >
                <img
                  src={brand.image}
                  alt={brand.name}
                  className="w-full h-full object-cover absolute inset-0 transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(44,26,14,0.85) 0%, rgba(44,26,14,0.05) 60%, transparent 100%)' }}></div>

                <div className="absolute top-4 left-4">
                  <span
                    className="text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full"
                    style={{ background: 'rgba(250,247,242,0.92)', color: '#3b2010' }}
                  >
                    {brand.badge}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3
                    className="font-bold mb-0.5"
                    style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.05rem', color: '#faf7f2' }}
                  >
                    {brand.name}
                  </h3>
                  <p className="text-xs italic" style={{ color: '#e8d5b7' }}>{brand.tagline}</p>
                  <div
                    className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold group-hover:gap-3 transition-all"
                    style={{ color: '#c9a96e' }}
                  >
                    Explore <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* View All Brands link */}
          <div className="text-center mt-10">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 font-semibold px-8 py-3 rounded-md text-sm transition-all whitespace-nowrap"
              style={{ border: '1px solid #c9a96e', color: '#3b2010', background: 'transparent' }}
            >
              View All Brands
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ── PRODUCTS SHOP GRID — warm cream ── */}
      <section style={{ background: 'linear-gradient(to bottom, #f5ede0, #faf7f2)' }} className="py-16 md:py-20">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20 max-w-6xl mx-auto">

          {/* ── Section Header ── */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px flex-1 max-w-[80px]" style={{ background: '#c9a96e' }}></div>
            <i className="ri-leaf-line text-base" style={{ color: '#c9a96e' }}></i>
            <h2
              className="font-bold text-center px-2"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.6rem', color: '#2c1a0e' }}
            >
              Our Premium Range
            </h2>
            <i className="ri-leaf-line text-base" style={{ color: '#c9a96e' }}></i>
            <div className="h-px flex-1 max-w-[80px]" style={{ background: '#c9a96e' }}></div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className="px-4 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap cursor-pointer"
                style={
                  activeCategory === cat
                    ? { background: '#3b2010', color: '#faf7f2', border: '1px solid #3b2010' }
                    : { background: 'transparent', color: '#7a4f2a', border: '1px solid #c9a96e' }
                }
              >
                {cat === 'all' ? 'All' : cat}
              </button>
            ))}
          </div>

          {/* Decorative divider */}
          <div className="flex items-center justify-center gap-2 mb-10">
            <div className="h-px w-24" style={{ background: '#e8d5b7' }}></div>
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#c9a96e' }}></div>
            <div className="h-px w-24" style={{ background: '#e8d5b7' }}></div>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop>
            {filteredProducts.slice(0, 10).map((product) => (
              <div
                key={product.id}
                className="group rounded-xl overflow-hidden transition-all duration-300 cursor-pointer"
                style={{ background: '#faf7f2', border: '1px solid #e8d5b7' }}
                onClick={() => navigate(`/products/${product.id}`)}
              >
                {/* Image */}
                <div className="relative aspect-square overflow-hidden" style={{ background: '#f5ede0' }}>
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {product.badge && (
                    <span
                      className="absolute top-2.5 left-2.5 text-[10px] font-bold px-2.5 py-1 rounded-full"
                      style={{ background: '#c9a96e', color: '#faf7f2' }}
                    >
                      {product.badge}
                    </span>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300" style={{ background: 'rgba(44,26,14,0.08)' }}>
                    <span
                      className="font-semibold px-4 py-1.5 rounded-full text-xs translate-y-2 group-hover:translate-y-0 transition-all duration-300 whitespace-nowrap"
                      style={{ background: '#faf7f2', color: '#3b2010', border: '1px solid #e8d5b7' }}
                    >
                      View Details
                    </span>
                  </div>
                </div>

                {/* Info */}
                <div className="p-3.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: '#c9a96e' }}>{product.brand}</p>
                  <h3
                    className="font-semibold text-sm leading-snug line-clamp-1 mb-3"
                    style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
                  >
                    {product.name}
                  </h3>
                  <Link
                    to={`/products/${product.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full text-xs font-semibold py-2.5 rounded-md transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                    style={{ background: '#3b2010', color: '#faf7f2', border: '1px solid #3b2010' }}
                  >
                    <i className="ri-arrow-right-line"></i>
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom divider + CTA */}
          <div className="flex items-center gap-4 mt-12 mb-6">
            <div className="h-px flex-1" style={{ background: '#e8d5b7' }}></div>
            <i className="ri-flower-line text-base" style={{ color: '#c9a96e' }}></i>
            <div className="h-px flex-1" style={{ background: '#e8d5b7' }}></div>
          </div>

          <div className="text-center">
            <p
              className="font-bold mb-5"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.1rem', color: '#2c1a0e' }}
            >
              Handcrafted with Love &amp; Care
            </p>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 font-semibold px-8 py-3 rounded-md text-sm transition-all whitespace-nowrap"
              style={{ background: '#3b2010', color: '#faf7f2' }}
            >
              View All Products
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <ProductQuickView product={quickViewProduct} onClose={() => setQuickViewProduct(null)} />
    </>
  );
}
